import React, { useMemo, forwardRef, useImperativeHandle, useEffect, useRef } from 'react'
import Switch from "Common/Custom/Switch";
import FormSelect from "Common/Custom/FormSelect";
import FormPhoneInput from "Common/Custom/FormPhoneInput";
import FormDatePicker from "Common/Custom/ReactFlatpickr";
import FormTextarea from "Common/Custom/FormTextarea";
import CountryName from "Common/json/CountryName.json";
import FormInput from "Common/Custom/FormInput";
import FormInputFile from "Common/Custom/FormInputFile";
import fields from "./json/CustomerFeilds.json";
import useFormSubmit from 'Hooks/useFormSubmit';
import { validateForm } from 'utils/validation';
import useFormChange from 'Hooks/useFormChange';
import { bookingTimeCustomerRules } from 'validation/ValidationRules';
import { createNewCustomer } from 'adminApi/RoomCheckInApi/RoomCheckIn';
import profileImage from "assets/images/avatar.png";
import { Modal, Button } from "react-bootstrap";
import DynamicAsyncSelect from "Common/Custom/DynamicAsyncSelect";
import FormSubmitButton from "Common/Custom/FormSubmitButton";
import { handleConfirm, normalizeCustomerOption } from "../HELPERS/Helpers";


// -------------------- ORIGINAL FORM AS IS --------------------
const CreateNewCustomerForm = forwardRef(({ newCustomer }, ref) => {
    const allFields = useMemo(
        () => [...fields.user_details, ...fields.contact_details, ...fields.identity_details, ...fields.customer_image],
        []
    );

    const initialValues = useMemo(() => {
        return allFields.reduce((acc, field) => {
            acc[field.name] = "";
            return acc;
        }, {});
    }, [allFields]);

    const { values, errors, handleChange, handleFileChange, setErrors } =
        useFormChange(initialValues, bookingTimeCustomerRules);

    const { loading: loadingSubmit, handleSubmit, response } = useFormSubmit(
        async (formValues) => {

            delete formValues.profile_imagePreview;
            delete formValues.img_backPreview;
            delete formValues.img_frontPreview;

            delete formValues.title;
            delete formValues.occupation;
            delete formValues.vip;
            delete formValues.state;
            delete formValues.comments;

            const formData = new FormData();
            Object.entries(formValues).forEach(([key, val]) => {
                formData.append(key, val ?? "");
            });
            return await createNewCustomer({ data: formData });
        },
        (vals) => {
            const err = validateForm(vals, bookingTimeCustomerRules);
            setErrors(err);
            return err;
        },
        () => '',
        "Customer Created successfully!"
    );

    useEffect(() => {
        if (response?.data) {
            newCustomer(response?.data);
        }
    }, [response, newCustomer]);

    const { user_details, contact_details, identity_details, customer_image } = fields;
    const countryOptions = CountryName.map((country) => ({
        value: country.name,
        label: country.name,
    }));
    const img_frontPreview = '';
    const img_backPreview = '';
    const img_profilePreview = profileImage;

    const renderField = (field) => {
        switch (field.type) {
            case "file":
                return (
                    <FormInputFile
                        key={field.name}
                        field={field}
                        handleChange={handleFileChange}
                        error={errors[field.name]}
                        imagePreview={field.name === "img_front" ? img_frontPreview : field.name === "img_back" ? img_backPreview : null || field.name === "profile_image" ? img_profilePreview : null}
                    />
                );
            case "switch":
                return (
                    <Switch
                        key={field.name}
                        values={values[field.name]}
                        handleChange={handleChange}
                        field={field}
                        ValueOne="1"
                        ValueTwo="0"
                        LabelOne="Active"
                        LabelTwo="Inactive"
                    />
                );
            case "select":
                return (
                    <FormSelect
                        key={field.name}
                        field={field}
                        value={values[field.name]}
                        handleChange={handleChange}
                        options={(field.name == "nationality" || field.name == "country") ? countryOptions : field.options}
                        error={errors[field.name]}
                    />
                );
            case "tel":
                return (
                    <FormPhoneInput
                        key={field.name}
                        field={field}
                        value={values[field.name]}
                        country_code={values['country_dial_code']}
                        handleChange={handleChange}
                        error={false}
                    />
                );
            case 'date':
                return (
                    <FormDatePicker
                        field={field}
                        value={values[field.name]}
                        handleChange={handleChange}
                        error={null}
                        dateFormat="Y-m-d"
                    />
                );
            case 'textarea':
                return (
                    <FormTextarea
                        key={field.name}
                        field={field}
                        value={values[field.name]}
                        handleChange={handleChange}
                        error={errors[field.name]}
                    />
                );
            case 'checkbox':
                return (
                    <div className={field.fieldSize || "col-12 mt-3"}>
                        <div className={`form-check ${field.inline ? "form-check-inline" : ""}`}>
                            <input
                                className={`form-check-input`}
                                type="checkbox"
                                id={field.name + "checkBox"}
                                name={field.name}
                                value={field.value}
                                disabled={field.disabled}
                                onChange={handleChange}
                            />

                            <label className="form-check-label" htmlFor={field.name + "checkBox"}>
                                {field.label}
                            </label>
                        </div>
                    </div>
                );
            default:
                return (
                    <FormInput
                        key={field.name}
                        field={field}
                        value={values[field.name]}
                        handleChange={handleChange}
                        error={errors[field.name]}
                    />
                );
        }
    };

    const testing = (e, values) => {
        handleSubmit(e, values)
    }

    useImperativeHandle(ref, () => ({
        submitForm: () => {
            const fakeEvent = { preventDefault: () => { } };
            handleSubmit(fakeEvent, values);
        }
    }));

    return (
        <form onSubmit={(e) => testing(e, values)}>
            <div className="row g-3 overflow-x-hidden">
                <div className="card">
                    <div className="card-header">
                        <h6 className="card-title mb-0">User Details</h6>
                    </div>
                    <div className="card-body">
                        <div className="row g-3">
                            {user_details.map((field, idx) => (
                                <React.Fragment key={`user_details-${field.name}-${idx}`}>
                                    {renderField(field)}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-header">
                        <h6 className="card-title mb-0">Contact Details</h6>
                    </div>
                    <div className="card-body">
                        <div className="row g-3">
                            {contact_details.map((field, idx) => (
                                <React.Fragment key={`contact_details-${field.name}-${idx}`}>
                                    {renderField(field)}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-header">
                        <h6 className="card-title mb-0">Identity Details</h6>
                    </div>
                    <div className="card-body">
                        <div className="row g-3">
                            {identity_details.map((field, idx) => (
                                <React.Fragment key={`identity_details-${field.name}-${idx}`}>
                                    {renderField(field)}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-header">
                        <h6 className="card-title mb-0">Customer Image</h6>
                    </div>
                    <div className="card-body">
                        <div className="row g-3">
                            {customer_image.map((field, idx) => (
                                <React.Fragment key={`customer_image-${field.name}-${idx}`}>
                                    {renderField(field)}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </form>
    )
})


// -------------------- FINAL WRAPPER COMPONENT --------------------
const CustomerFormModal = ({
    show,
    setShow,
    handleExited,
    title,
    customerType,
    rootContainer,
    setSelectedCustomer,
    setSelectedCustomerId,
    selectedCustomerId,
    setNewCustomer,
    setValues,
    selectedCustomer,
    newCustomer,
    setExistingCustomers
}) => {

    const formRef = useRef(null);

    return (
        <Modal
            container={rootContainer}
            style={{ zIndex: 9999 }}
            show={show} onHide={() => setShow(false)}
            onExited={handleExited}
            centered
            backdrop={true}
            size={customerType === "new" ? "lg" : ""}
        >
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {customerType === "existing" && (
                    <div className="mb-5">
                        <label className="form-label">Select Customer</label>
                        <DynamicAsyncSelect
                            onChange={(option) => {
                                const norm = normalizeCustomerOption(option);
                                const id = norm?.customer_id || "";
                                setSelectedCustomerId(id);
                                setSelectedCustomer(norm || {});
                                setValues((prev) => ({ ...prev, customer_id: id }));
                            }}
                            value={selectedCustomerId}
                            placeholder="Select Customer"
                        />
                    </div>
                )}

                {customerType === "new" && (
                    <CreateNewCustomerForm
                        newCustomer={setNewCustomer}
                        ref={formRef}
                        SubmitButtonLabel={
                            <FormSubmitButton
                                className=""
                                label="Create"
                                colSize="col-12"
                                align="end"
                            />
                        }
                    />
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShow(false)}>
                    Cancel
                </Button>
                {customerType === "existing" && (
                    <Button
                        variant="danger"
                        onClick={() => handleConfirm(
                            selectedCustomerId,
                            selectedCustomer,
                            customerType,
                            setExistingCustomers,
                            setValues,
                            newCustomer,
                            setShow,
                            setSelectedCustomer
                        )}
                        disabled={(customerType === "existing" && !selectedCustomerId)}
                    >
                        Yes
                    </Button>
                )}
                {customerType === "new" && (
                    <Button
                        variant="primary"
                        onClick={() => formRef.current?.submitForm()}
                    >
                        Create Customer
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default CustomerFormModal;
